import { useState, useRef, useEffect } from 'react';
import './ReminderTemplates.css';
import { scopedKey, isBuiltinPublic, DEFAULT_SYSTEM_MESSAGES } from './scope';
import { applyAutoEnrollAssignments, ADHOC_INTERVALS, INTERVAL_LABELS } from './assignmentUtils';
import RichEditor, { hydrateVarPills } from './RichEditor';
import { visibleFiles, fmtSize, openFile } from './fileLib';
import { putScoped } from './store/syncState';
import { usingApi } from './api';
import Icon from './Icon';

// Render stored template HTML with {{tokens}} shown as the same styled pills the
// editor uses, so an inherited (read-only) reminder looks identical to the editor.
function htmlWithVarPills(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  hydrateVarPills(tmp);
  return tmp.innerHTML;
}

// Cache a reminder doc locally AND return the awaited server-save promise. These keys
// are no longer auto-mirrored, so the save action holds the real request — reliable on
// iOS, exactly like the contact/property forms.
function saveDoc(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch { /* quota — server save still runs */ }
  return putScoped(key, obj);
}

// Built-in date-anchored reminders. Each carries only the variables that make
// sense for it — they are NOT given the holiday date-tokens (the reminder already
// IS its date), so the insert bar stays uncluttered.
const SYSTEM_TEMPLATES = [
  { id: 'new-closing', label: 'New Closing', icon: 'type-new-closing', description: 'Congratulate a client 1–4 weeks after their closing — one time, never for old imported sales.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Property}}', '{{Sold Date}}'] },
  { id: 'sale-anniversary', label: 'Sale Anniversary', icon: 'type-sale-anniversary', description: 'Celebrate the anniversary of a client\'s home purchase.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Property}}', '{{Sold Date}}', '{{Nth}}', '{{Years}}'] },
  { id: 'happy-birthday',    label: 'Happy Birthday',   icon: 'type-birthday', description: 'Wish clients a happy birthday with a personal touch.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Birthday}}'] },
  { id: 'holiday-new-year',     label: 'New Year',         icon: 'type-new-year', description: 'New Year greeting — sends to everyone on January 1.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}'] },
  { id: 'holiday-valentines',   label: "Valentine's Day",  icon: 'type-valentines', description: "Valentine's greeting — sends to everyone on February 14.", variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}'] },
  { id: 'holiday-july4',        label: 'Independence Day', icon: 'type-july4', description: 'Fourth of July greeting — sends to everyone on July 4.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}'] },
  { id: 'holiday-thanksgiving', label: 'Thanksgiving',     icon: 'type-thanksgiving', description: 'Thanksgiving greeting — sends on the 4th Thursday of November.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}'] },
  { id: 'holiday-christmas',    label: 'Christmas',        icon: 'type-christmas', description: 'Christmas greeting — sends to everyone on December 25.', variables: ['{{Contact}}', '{{First Name}}', '{{Last Name}}'] },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '汉语' },
  { code: 'es', label: 'El español' },
];

// Master label map for every insertable variable.
const VARIABLES = [
  { key: '{{Contact}}',    label: 'Contact' },
  { key: '{{First Name}}', label: 'First Name' },
  { key: '{{Last Name}}',  label: 'Last Name' },
  { key: '{{Email}}',      label: 'Email' },
  { key: '{{Phone}}',      label: 'Phone' },
  { key: '{{Birthday}}',   label: 'Birthday' },
  { key: '{{Property}}',   label: 'Property Address' },
  { key: '{{City}}',       label: 'City' },
  { key: '{{State}}',      label: 'State' },
  { key: '{{Zip}}',        label: 'Zip' },
  { key: '{{Sold Date}}',  label: 'Sold Date' },
  { key: '{{Nth}}',        label: 'Anniversary (Nth, e.g. 3rd)' },
  { key: '{{Years}}',      label: 'Years in home (e.g. 3)' },
  { key: '{{Price}}',      label: 'Price' },
  { key: '{{Agent}}',      label: 'Agent' },
  // Special date / holiday system variables.
  { key: '{{New Year}}',        label: 'New Year' },
  { key: "{{Valentine's Day}}", label: "Valentine's Day" },
  { key: '{{July 4}}',          label: 'July 4' },
  { key: '{{Thanksgiving}}',    label: 'Thanksgiving' },
  { key: '{{Christmas}}',       label: 'Christmas' },
];

// Variable groups offered by each kind of reminder. Holiday date-tokens
// ({{New Year}}, {{Christmas}}, …) are intentionally NOT offered here: they
// aren't part of the base "Reminder Template" (varGroup 'all'), so copies and
// custom reminders don't surface them either.
const VAR_GROUPS = {
  contact:  ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Email}}', '{{Phone}}', '{{Birthday}}', '{{Property}}', '{{Sold Date}}'],
  property: ['{{Property}}', '{{City}}', '{{State}}', '{{Zip}}', '{{Sold Date}}', '{{Price}}', '{{Contact}}'],
  agent:    ['{{Agent}}'],
  // Holiday greeting templates: just a greeting name + the agent's name.
  holiday:  ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Agent}}'],
  // The general "Reminder template" — ALL contact + property fields, no date tokens.
  all:      ['{{Contact}}', '{{First Name}}', '{{Last Name}}', '{{Email}}', '{{Phone}}', '{{Birthday}}', '{{Property}}', '{{City}}', '{{State}}', '{{Zip}}', '{{Sold Date}}', '{{Price}}'],
};
function varsForGroup(group) {
  return [...(VAR_GROUPS[group] || VAR_GROUPS.contact)];
}

function ReminderTemplates({ isAdmin = false, scope, systemLevel = false, showAllBuiltins = false, suspendScope, suspended = false, orgSuspended = false, onToggleSuspend, onPush, fileKeys = { ownKeys: [], publicKeys: [] }, pushUnit, onPropagateReminder }) {
  // What a push targets, for button/status wording: System→MLS, MLS→organization, Org→agent.
  const pushUnitLabel = pushUnit || (systemLevel ? 'MLS' : 'agent');
  const K_TEMPLATES = scopedKey('ira_templates', scope);
  const K_CUSTOM    = scopedKey('ira_custom_templates', scope);
  const K_AUTOENROLL = scopedKey('ira_autoenroll', scope);
  const K_ATTACH    = scopedKey('ira_attachments', scope);   // { [reminderId]: [fileId, …] } refs into ira_files
  // Admins/sysadmin edit the "master" set for their level; the public/private flag
  // on each reminder controls whether it cascades to the next level down.
  const isMaster = isAdmin;
  // Auto-Enrollable is a per-reminder flag at EVERY level (system / org / agent),
  // default OFF. It cascades system → org → agent (seeded on creation) and each level
  // can override it. The map records only the reminders switched ON.
  const [autoMap, setAutoMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_AUTOENROLL) || '{}'); } catch { return {}; }
  });
  const isAuto = (id) => autoMap[id] === true;
  function toggleAutoEnroll(id) {
    const next = { ...autoMap };
    if (next[id] === true) delete next[id]; else next[id] = true;   // ON, or back to OFF (default)
    saveDoc(K_AUTOENROLL, next);
    setAutoMap(next);
    // Materialize/clear this scope's contact assignments to match the new flag:
    // turning auto ON assigns the reminder to all eligible contacts; turning it OFF
    // removes the auto-stamped assignments. (No-op for scopes without contacts.)
    applyAutoEnrollAssignments(scope);
  }
  const [selected, setSelected]     = useState(null);
  const [language, setLanguage]     = useState('en');
  const [messages, setMessages]     = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_TEMPLATES) || '{}'); } catch { return {}; }
  });
  const [customTemplates, setCustomTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_CUSTOM) || '[]'); } catch { return []; }
  });
  const [savedKeys, setSavedKeys]   = useState({});
  const [pushStatus, setPushStatus] = useState('');
  const [pushPrompt, setPushPrompt] = useState(null);   // reminder id awaiting save/discard before push
  const [pendingNav, setPendingNav] = useState(null);   // pending card-switch blocked by unsaved changes
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  // Per-reminder attachments are now arrays of file-ID REFERENCES into the shared
  // library (ira_files) — the file bytes are never copied here.
  const [attachments, setAttachments] = useState(() => {
    try { return JSON.parse(localStorage.getItem(K_ATTACH) || '{}'); } catch { return {}; }
  });
  const editorRef = useRef(null);
  const importInputRef = useRef(null);
  const [importMsg, setImportMsg] = useState('');
  const [saveNote, setSaveNote] = useState('');   // transient "Saving…/✓ Saved" confirmation for copy/create
  const [saving, setSaving] = useState(false);    // a Save/Add is in flight — block the button until the server confirms
  // Await the actual server-save promises (held directly, like the contact/property
  // forms) so a save can't silently fail on iOS where detached background requests get
  // dropped. `saves` is an array of putScoped promises returned by saveDoc().
  async function finishSave(saves) {
    if (!usingApi() || !saves || !saves.length) return true;
    setSaving(true);
    let ok = true;
    try { ok = (await Promise.all(saves)).every((r) => r !== false); }
    catch { ok = false; }
    setSaving(false);
    if (!ok) setSaveNote('⚠ Not synced yet — still retrying');
    return ok;
  }
  // Auto-clear the "✓ Saved" confirmation after a few seconds.
  useEffect(() => {
    if (!saveNote.startsWith('✓')) return;
    const id = setTimeout(() => setSaveNote(''), 3500);
    return () => clearTimeout(id);
  }, [saveNote]);
  // Draft state for the panel — buffered until Save is clicked.
  const [draftLabel, setDraftLabel]           = useState('');
  const [draftAttachments, setDraftAttachments] = useState([]);
  const [draftPublic, setDraftPublic]         = useState(true);   // public/private flag, buffered like the rest
  const [draftInterval, setDraftInterval]     = useState('One Time');   // repeating cadence (custom reminders)

  // Sync drafts when the selected reminder changes.
  useEffect(() => {
    if (!selected) return;
    const c = customTemplates.find(t => t.id === selected);
    const sys = SYSTEM_TEMPLATES.find(t => t.id === selected);
    setDraftLabel((c || sys)?.label || '');
    setDraftAttachments(attachments[selected] ? [...attachments[selected]] : []);
    setDraftPublic(savedPublicFor(selected, !!sys));
    setDraftInterval((c && c.interval) || 'One Time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Real custom reminders (exclude built-in public/private override entries).
  const realCustom = customTemplates.filter(c => !c.builtin);

  // At System/MLS scope (showAllBuiltins) the full list is always visible so the
  // admin can manage visibility. At org/agent scope only show built-ins that have
  // actually been inherited (have a customTemplates override entry).
  const visibleBuiltins = showAllBuiltins
    ? SYSTEM_TEMPLATES
    : SYSTEM_TEMPLATES.filter(t => customTemplates.some(c => c.builtin && c.id === t.id));

  const allTemplates = [
    ...visibleBuiltins.map(t => ({ ...t, inherited: customTemplates.find(c => c.builtin && c.id === t.id)?.inherited === true })),
    ...realCustom.map(c => ({
      id: c.id, label: c.label, icon: c.holiday ? 'gift' : (c.base ? 'type-custom' : 'type-custom'),
      description: c.holiday ? 'Holiday template — date-driven, not based on client data.' : c.base ? 'Base template — duplicate it to build a new reminder.' : 'Custom reminder.',
      custom: true, base: !!c.base, holiday: !!c.holiday, inherited: !!c.inherited,
      anchor: c.anchor || null,   // copy of a built-in → its date anchor; plain custom → null
      interval: c.interval || null,   // repeating cadence for plain (interval-type) customs
      varGroup: c.varGroup || 'contact',
      isPublic: c.public !== false,
      variables: Array.isArray(c.variables) ? c.variables : varsForGroup(c.varGroup || 'contact'),
    })),
  ];

  const activeType = allTemplates.find(t => t.id === selected);
  // Inherited reminders are READ-ONLY templates from an upper level: their CONTENT
  // isn't editable and they're not assignable here. To use one, the level makes its
  // own copy. (The public/private RELAY flag is still editable — see canEditPublic.)
  const readOnly = !!activeType?.inherited;
  // Public/private is editable in the detail panel by admins, for any non-base
  // reminder — INCLUDING inherited ones. It only controls whether this level relays
  // the reminder to the level below; it never changes the reminder's content. (Base
  // scaffolds are system-level and can't be published; agents have no level below.)
  const canEditPublic = () => isAdmin && !!activeType && !activeType.base;
  // The SAVED public state for a reminder, read from the stored entry (not from the
  // mapped activeType, whose custom rows expose `isPublic`, not `public`).
  function savedPublicFor(id, isBuiltin) {
    if (isBuiltin) return isBuiltinPublic(customTemplates, id);
    const c = customTemplates.find((x) => x.id === id && !x.builtin);
    return c ? c.public !== false : true;
  }
  // The repeating interval (cadence) is editable only for CUSTOM reminders — i.e.
  // those that originated from "+ Add Custom Reminder" (no date anchor) — when not
  // read-only. Built-ins and copies of built-ins are date-anchored + annual.
  const canEditInterval = () => !!activeType && activeType.custom && !activeType.base && !activeType.anchor && !activeType.inherited;
  function savedIntervalFor(id) {
    const c = customTemplates.find((x) => x.id === id && !x.builtin);
    return (c && c.interval) || 'One Time';
  }
  const msgKey = selected ? `${selected}__${language}` : null;
  // Built-in (and anchored-copy) reminders fall back to the canonical default body
  // when this scope has no stored override — so the editor/preview show the real
  // message the engine will send, instead of looking empty at a fresh scope.
  const defaultFor = (id, anchor, lang) => {
    const fb = anchor || id;
    const d = fb && DEFAULT_SYSTEM_MESSAGES[fb];
    return d ? (d[lang] || d.en || '') : '';
  };
  const storedMessage = msgKey ? (messages[msgKey] ?? '') : '';
  const currentMessage = (storedMessage && storedMessage.replace(/<[^>]+>/g, '').trim())
    ? storedMessage
    : (selected ? defaultFor(selected, activeType && activeType.anchor, language) : '') || storedMessage;

  // Returns the array of awaited server-save promises so callers can confirm they landed.
  function saveToStorage(msgs, customs) {
    return [saveDoc(K_TEMPLATES, msgs), saveDoc(K_CUSTOM, customs)];
  }

  function handleCardClick(id) {
    if (id === selected) return;
    if (hasUnsavedChanges()) { setPendingNav({ id }); return; }
    setSelected(id);
    setLanguage('en');
    setDeleteConfirmId(null);
    setPushStatus('');
    setPushPrompt(null);
  }

  // Push this reminder down a level (system → orgs, org → agents). Add-only.
  // Push acts on the SAVED (stored) reminder, so if the editor has unsaved message
  // edits we first prompt to save or discard them.
  function reminderUnsaved(id) {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(K_TEMPLATES) || '{}'); } catch {}
    return LANGUAGES.some(l => (messages[`${id}__${l.code}`] ?? '') !== (stored[`${id}__${l.code}`] ?? ''));
  }
  async function performPush(id) {
    const unit = pushUnitLabel;
    setPushStatus('Pushing…');
    const r = (onPush && await onPush(id)) || { pushed: 0, total: 0 };
    if (r.total === 0) setPushStatus(`No ${unit}s to push to yet.`);
    else setPushStatus(`✓ Copied to ${r.pushed} ${unit}${r.pushed === 1 ? '' : 's'}.`);
  }
  function doPush(id) {
    if (!onPush) return;
    setPushStatus('');
    if (reminderUnsaved(id)) { setPushPrompt(id); return; }   // ask to save/discard first
    performPush(id);
  }
  async function savePushThenPush() {
    if (msgKey) setSavedKeys(prev => ({ ...prev, [msgKey]: true }));
    const id = pushPrompt; setPushPrompt(null);
    await finishSave([saveDoc(K_TEMPLATES, messages)]);   // persist edits (awaited) before pushing
    performPush(id);
  }
  function discardThenPush() {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(K_TEMPLATES) || '{}'); } catch {}
    setMessages(stored);            // drop unsaved edits
    setSavedKeys({});
    const id = pushPrompt; setPushPrompt(null);
    performPush(id);                // pushes the saved (stored) version
  }

  // Returns true if the panel has any unsaved edits (label, message body, attachments).
  function hasUnsavedChanges() {
    if (!selected) return false;
    if (reminderUnsaved(selected)) return true;
    if (activeType?.custom && !activeType?.base && draftLabel !== activeType.label) return true;
    const stored = attachments[selected] || [];
    if (draftAttachments.length !== stored.length || draftAttachments.some(id => !stored.includes(id))) return true;
    if (canEditPublic() && draftPublic !== savedPublicFor(selected, activeType && !activeType.custom)) return true;
    if (canEditInterval() && draftInterval !== savedIntervalFor(selected)) return true;
    return false;
  }
  // Roll back the current reminder's message body to stored state (for Discard).
  function discardCurrentChanges() {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(K_TEMPLATES) || '{}'); } catch {}
    const reset = { ...messages };
    LANGUAGES.forEach(l => {
      const k = `${selected}__${l.code}`;
      if (stored[k] != null) reset[k] = stored[k]; else delete reset[k];
    });
    setMessages(reset);
    setSavedKeys(prev => ({ ...prev, [msgKey]: true }));
    // draftLabel / draftAttachments reset automatically via the useEffect([selected]) below.
  }
  // Execute the navigation that was blocked by unsaved changes.
  function executePendingNav(nav) {
    setPendingNav(null);
    if (!nav) return;
    setSelected(nav.id);
    setLanguage('en');
    setDeleteConfirmId(null);
    setPushStatus('');
    setPushPrompt(null);
  }

  function handleMessageChange(html) {
    setMessages(prev => ({ ...prev, [msgKey]: html }));
    setSavedKeys(prev => ({ ...prev, [msgKey]: false }));
  }

  async function handleSave() {
    // Inherited (read-only) reminders: only the public/private RELAY flag is editable
    // — it controls whether THIS level passes the reminder to the level below, and
    // changes nothing about the content. Update just that flag on the inherited entry:
    // keep `inherited:true`, never set `modified` (so the parent's content keeps
    // syncing down), and don't touch messages/attachments (the cascade owns those).
    if (activeType?.inherited) {
      if (canEditPublic()) {
        const isBuiltin = !activeType.custom;
        if (draftPublic !== savedPublicFor(selected, isBuiltin)) {
          let next;
          if (isBuiltin) {
            const existing = customTemplates.find(c => c.builtin && c.id === selected);
            const without = customTemplates.filter(c => !(c.builtin && c.id === selected));
            next = [...without, { ...(existing || { id: selected, builtin: true }), inherited: true, public: draftPublic }];
          } else {
            next = customTemplates.map(c => c.id === selected ? { ...c, inherited: true, public: draftPublic } : c);
          }
          setCustomTemplates(next);
          setSavedKeys(prev => ({ ...prev, [msgKey]: true }));
          const ok = await finishSave([saveDoc(K_CUSTOM, next)]);   // await the real request
          if (ok && draftPublic && onPropagateReminder) onPropagateReminder(selected);   // relay it down only after it saved
          return;
        }
      }
      setSavedKeys(prev => ({ ...prev, [msgKey]: true }));
      return;
    }

    const saves = [];
    // Message body
    saves.push(saveDoc(K_TEMPLATES, { ...messages }));
    setSavedKeys(prev => ({ ...prev, [msgKey]: true }));

    // Resolve the public/private flag from the buffered draft (admins only; base
    // scaffolds can't change it — pubNow falls back to saved).
    const isBuiltin = activeType && !activeType.custom;   // system built-in (no custom entry)
    const labelChanged = activeType?.custom && !activeType?.base && draftLabel !== activeType.label;
    const editPublic = canEditPublic();
    const savedPublic = savedPublicFor(selected, isBuiltin);
    const publicChanged = editPublic && draftPublic !== savedPublic;
    const pubNow = editPublic ? draftPublic : savedPublic;
    const intervalChanged = canEditInterval() && draftInterval !== savedIntervalFor(selected);

    // Fold label + public + "modified" into ONE customTemplates write (no races).
    // `modified` marks this scope's copy as locally owned so upstream propagations
    // won't overwrite it.
    let next = customTemplates;
    if (isBuiltin) {
      if (publicChanged || customTemplates.some(c => c.builtin && c.id === selected)) {
        const existing = customTemplates.find(c => c.builtin && c.id === selected);
        const without = customTemplates.filter(c => !(c.builtin && c.id === selected));
        next = [...without, { ...(existing || {}), id: selected, builtin: true, public: pubNow, modified: true }];
      }
    } else {
      const hasEntry = customTemplates.some(c => c.id === selected);
      if (hasEntry || labelChanged || publicChanged || intervalChanged) {
        next = customTemplates.map(c => {
          if (c.id !== selected) return c;
          const u = { ...c, modified: true };
          if (labelChanged) u.label = draftLabel;
          if (publicChanged) u.public = draftPublic;
          if (intervalChanged) u.interval = draftInterval;
          return u;
        });
      }
    }
    if (next !== customTemplates) {
      setCustomTemplates(next);
      saves.push(saveDoc(K_CUSTOM, next));
    }

    // Attachments
    const nextAttach = { ...attachments };
    if (draftAttachments.length) nextAttach[selected] = [...draftAttachments];
    else delete nextAttach[selected];
    saves.push(persistAttachments(nextAttach));

    // Wait for all writes to actually reach the server before relaying down.
    const ok = await finishSave(saves);

    // Propagate to sub-scopes if this reminder is (now) public and the prop is wired (MLS level).
    if (ok && onPropagateReminder && activeType && pubNow) onPropagateReminder(selected);
  }

  function insertVariable(varKey, label) {
    if (editorRef.current) editorRef.current.insertVar(varKey, label);
  }

  // ── Attachments: select files from the shared library (own + inherited public). ──
  function persistAttachments(next) {
    setAttachments(next);
    return saveDoc(K_ATTACH, next);
  }
  // Buffered — only written to storage on Save.
  function toggleAttachDraft(fileId) {
    setDraftAttachments(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  }

  // Append a new custom reminder, optionally copying a source reminder's message
  // bodies, then select it. Shared by brand-new creation and Duplicate.
  async function commitCustom(entry, sourceId) {
    const updated = [...customTemplates, entry];
    let msgs = messages;
    if (sourceId) {
      msgs = { ...messages };
      // The fallback id for a built-in (or a copy of a built-in, which carries `anchor`)
      // so a copy still gets the canonical default body if this scope never materialized
      // the source's body (e.g. a freshly-created org/agent where built-in defaults
      // weren't floated in). Plain customs have no default → nothing to fall back to.
      const fallbackId = entry.anchor || sourceId;
      LANGUAGES.forEach(l => {
        let v = messages[`${sourceId}__${l.code}`];
        if (v == null || String(v).replace(/<[^>]+>/g, '').trim() === '') {
          const def = DEFAULT_SYSTEM_MESSAGES[fallbackId] && DEFAULT_SYSTEM_MESSAGES[fallbackId][l.code];
          if (def != null) v = def;
        }
        if (v != null) msgs[`${entry.id}__${l.code}`] = v;
      });
      setMessages(msgs);
    }
    const saves = [];
    // Carry the source's attachment references onto the copy (references, not bytes).
    if (sourceId && (attachments[sourceId] || []).length) {
      saves.push(persistAttachments({ ...attachments, [entry.id]: [...attachments[sourceId]] }));
    }
    setCustomTemplates(updated);
    saves.push(...saveToStorage(msgs, updated));   // explicit AWAITED saves (held, not detached)
    setSelected(entry.id);
    setLanguage('en');
    // Confirm the copy actually reached the central DB (awaits the real requests) and
    // tell the user — so a quick refresh can't make a just-made copy look lost.
    if (usingApi()) {
      setSaveNote('Saving…');
      const ok = await finishSave(saves);
      setSaveNote(ok ? '✓ Saved to your account' : '⚠ Not synced yet — still retrying');
    }
  }

  // Instantly create a new reminder and select it — no name prompt — exactly like
  // duplicating the base "Reminder Template". The name and everything else are then
  // edited inline in the detail panel.
  function createNew() {
    const id = `custom__${Date.now()}`;
    const entry = { id, label: 'New Custom Reminder', varGroup: 'all' };   // 'all' = base palette
    if (isAdmin) entry.public = false;        // start private; publish is a deliberate action
    commitCustom(entry, null);
  }

  // ── Import / Export a single reminder (definition + variable list + messages) ──
  // A unique label at THIS scope: "Name", then "Name_1", "Name_2", …
  function freeLabelHere(base) {
    const taken = new Set(customTemplates.map(c => c.label).filter(Boolean));
    if (!taken.has(base)) return base;
    let i = 1; while (taken.has(`${base}_${i}`)) i++; return `${base}_${i}`;
  }
  function exportReminder(id) {
    const t = allTemplates.find(x => x.id === id);
    if (!t) return;
    const msgsOut = {};
    LANGUAGES.forEach(l => { const v = messages[`${id}__${l.code}`]; if (v != null) msgsOut[l.code] = v; });
    const payload = {
      iraReminder: 1,
      label: t.label,
      varGroup: t.varGroup || 'contact',
      variables: Array.isArray(t.variables) ? t.variables : varsForGroup(t.varGroup || 'contact'),
      messages: msgsOut,
    };
    if (t.anchor) payload.anchor = t.anchor;       // preserve a copy-of-built-in's date anchor
    if (t.interval) payload.interval = t.interval; // preserve a custom reminder's cadence
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(t.label || 'reminder').replace(/[^\w.-]+/g, '_')}.ira-reminder.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function onImportFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';   // allow re-importing the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let data;
      try { data = JSON.parse(String(reader.result)); } catch { setImportMsg('⚠ That file isn’t a valid reminder export.'); return; }
      if (!data || typeof data.label !== 'string') { setImportMsg('⚠ That file isn’t a valid reminder export.'); return; }
      // Always an OWNED reminder at THIS scope (new id), renamed on a label collision.
      const id = `imp_${Date.now()}`;
      const entry = { id, label: freeLabelHere(data.label), varGroup: data.varGroup || 'contact' };
      if (Array.isArray(data.variables)) entry.variables = [...data.variables];
      if (data.anchor) entry.anchor = data.anchor;   // imported copy of a built-in stays date-anchored
      if (isAdmin) entry.public = false;
      const msgs = { ...messages };
      if (data.messages && typeof data.messages === 'object') {
        LANGUAGES.forEach(l => { const v = data.messages[l.code]; if (typeof v === 'string') msgs[`${id}__${l.code}`] = v; });
      }
      const updated = [...customTemplates, entry];
      setMessages(msgs); setCustomTemplates(updated); saveToStorage(msgs, updated);
      setSelected(id); setLanguage('en');
      setImportMsg(`✓ Imported “${entry.label}”.`);
    };
    reader.readAsText(file);
  }

  // Rename a custom reminder (e.g. a copy) — editable in the detail panel, like the
  // message body. Persists immediately; built-ins/base scaffolds aren't renamable.
  function renameCustom(id, label) {
    const updated = customTemplates.map(c => c.id === id ? { ...c, label } : c);
    setCustomTemplates(updated);
    saveDoc(K_CUSTOM, updated);
  }

  // Duplicate a reminder instantly: create "Copy of <name>" (private), copy its
  // messages, and select it — the user stays right here and edits the copy in place
  // (its name is editable inline in the detail panel, like the message body).
  function startDuplicate(id) {
    const src = allTemplates.find(t => t.id === id);
    if (!src) return;
    const entry = { id: `custom__${Date.now()}`, label: `Copy of ${src.label}`, varGroup: src.varGroup || 'contact' };
    if (Array.isArray(src.variables)) entry.variables = [...src.variables];
    // A copy of a date-anchored SYSTEM reminder keeps that anchor, so the engine still
    // fires it on the birthday / sold-date / holiday and it defaults to Annual. (A
    // built-in anchors to itself; a copy-of-a-copy carries the original anchor forward.
    // Plain "+ Custom Reminder" reminders have no anchor → interval-driven, One Time.)
    const srcCustom = customTemplates.find(c => c.id === id);
    const srcAnchor = SYSTEM_TEMPLATES.some(t => t.id === id) ? id : (srcCustom?.anchor || null);
    if (srcAnchor) entry.anchor = srcAnchor;                  // copy of a built-in → date-anchored, annual
    else if (srcCustom?.interval) entry.interval = srcCustom.interval;   // copy of a plain custom → carry its cadence
    if (isAdmin) entry.public = false;   // copies always start private
    commitCustom(entry, id);
  }

  function handleDeleteCustom(id) {
    const target = customTemplates.find(c => c.id === id);
    if (target && target.base) return;   // base scaffolds are system templates — not deletable
    const updated = customTemplates.filter(c => c.id !== id);
    setCustomTemplates(updated);
    // Remove all message keys for this template
    const updatedMsgs = { ...messages };
    Object.keys(updatedMsgs).forEach(k => { if (k.startsWith(id + '__')) delete updatedMsgs[k]; });
    setMessages(updatedMsgs);
    saveToStorage(updatedMsgs, updated);
    if (attachments[id]) { const next = { ...attachments }; delete next[id]; persistAttachments(next); }
    if (selected === id) setSelected(null);
    setDeleteConfirmId(null);
  }

  // Auto-Enroll toggle that lives ON the card (top-right). It saves immediately
  // (no Save needed). Auto-Enroll is an AGENT-ONLY flag — it only does something
  // where there are contacts to enroll — so it isn't shown at System/MLS/Org level.
  const autoTitle = 'When on, this reminder is automatically applied to all eligible contacts. You can still override individual contacts on the Assigned Reminders page.';
  const renderAutoToggle = (id) => (
    <label className="rt-auto-toggle" title={autoTitle} onClick={e => e.stopPropagation()}>
      <input type="checkbox" checked={isAuto(id)} onChange={() => toggleAutoEnroll(id)} />
      <span className="lbl-full">Auto-Enroll</span><span className="lbl-short">Auto</span>
    </label>
  );
  // Read-only visibility LABEL on the card (admins): shows the SAVED relay state so you
  // can see at a glance what's visible to the level below without opening the editor.
  // Label only — the editable toggle lives in the detail panel and only this updates
  // (from the stored entry) once a change is Saved.
  const renderPublicLabel = (type, builtin) => {
    const pub = savedPublicFor(type.id, builtin);
    return (
      <span className={`rt-pub-label ${pub ? 'on' : 'off'}`}
        title={pub ? 'Public — relayed to the level below.' : 'Private — not relayed to the level below.'}>
        <Icon name={pub ? 'public' : 'private'} size={16} /><span className="lbl-full">{pub ? ' Public' : ' Private'}</span>
      </span>
    );
  };

  // The card's top-right chip stack: the read-only badge (inherited), the saved
  // visibility label (admins), and the agent-only Auto-Enroll toggle. Base scaffolds
  // get none. The editable public/private toggle itself lives in the detail panel.
  const renderCardToggles = (type, builtin) => {
    const inherited = builtin ? (customTemplates.find(c => c.builtin && c.id === type.id)?.inherited === true) : !!type.inherited;
    const showAuto = !type.base && !isAdmin && !inherited;   // Auto-Enroll: agent-only, OWNED reminders only
    const showPubLabel = isAdmin && !type.base;              // saved-visibility indicator (label only)
    if (!inherited && !showAuto && !showPubLabel) return null;
    return (
      <div className="rt-card-toggles">
        {inherited && <span className="rt-inherited-badge" title="Inherited from an upper level — read-only. Make a copy to edit or assign."><Icon name="lock" size={16} /> Read-only</span>}
        {showPubLabel && renderPublicLabel(type, builtin)}
        {showAuto && renderAutoToggle(type.id)}
      </div>
    );
  };

  // Base scaffolds render first (before the built-ins); other customs render last.
  const baseCards  = realCustom.filter(c => c.base);
  const otherCards = realCustom.filter(c => !c.base);
  // A reminder is "empty" when it has no message body in ANY language — the engine
  // skips such reminders ("no message template"), so they silently never send. Flag
  // them on the card. Base templates are scaffolds to duplicate, so they're exempt.
  const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
  // Has a real message in some language — OR a built-in/anchored default to fall back
  // on (so built-ins are never flagged as empty even at a fresh scope).
  const hasContent = (id, anchor) =>
    LANGUAGES.some(l => stripHtml(messages[`${id}__${l.code}`]).length > 0)
    || !!DEFAULT_SYSTEM_MESSAGES[anchor || id];
  const noMsgBadge = () => (
    <span className="rt-card-warn" title="This reminder has no message — it will be skipped (won't send) until you add content in the editor.">⚠ No message</span>
  );

  const renderCustomCard = (type) => (
    <div key={type.id} className={`rt-card rt-card-custom ${type.base ? 'rt-card-base' : type.inherited ? 'rt-card-inherited' : type.holiday ? 'rt-card-holiday' : ''} ${selected === type.id ? 'active' : ''}`}>
      <button className="rt-card-custom-body" onClick={() => handleCardClick(type.id)}>
        <div className="rt-card-custom-top">
          <div className="rt-card-icon-row">
            <span className="rt-card-icon"><Icon name={type.holiday ? 'gift' : type.base ? 'type-custom' : 'type-custom'} size={20} /></span>
            {/* Holiday / Base-template badges (owner's own tab only). */}
            {!type.inherited && (type.holiday || type.base) && (
              <span className={`rt-custom-badge ${type.holiday ? 'rt-holiday-badge' : ''} ${type.base && !type.holiday ? 'rt-base-badge' : ''}`}>{type.holiday ? 'Holiday' : 'Base template'}</span>
            )}
            {/* "Custom" marks a reminder that ORIGINATED from "+ Add Custom Reminder"
                (interval-type) — and its copies. NOT copies of system built-ins (those
                carry a date anchor + are annual). This is independent of Read-only:
                an inherited custom shows BOTH 🔒 Read-only and Custom. */}
            {!type.base && !type.holiday && !type.anchor && (
              <span className="rt-custom-badge">Custom</span>
            )}
          </div>
        </div>
        <span className="rt-card-label">{type.label}</span>
        <span className="rt-card-desc">{type.holiday ? 'Date-driven greeting — not based on client data.' : type.base ? 'Duplicate this to build a new reminder.' : 'User-defined reminder template.'}</span>
        {!type.base && !hasContent(type.id, type.anchor) && noMsgBadge()}
      </button>
      {renderCardToggles(type, false)}
      {!type.base && !type.inherited && (
        <button className="rt-card-delete-btn" title="Delete template"
          onClick={e => { e.stopPropagation(); setDeleteConfirmId(type.id); }}><Icon name="delete" size={16} /></button>
      )}
      {deleteConfirmId === type.id && (
        <div className="rt-delete-confirm-overlay" onClick={e => e.stopPropagation()}>
          <span>Delete <strong>{type.label}</strong>?</span>
          <div className="rt-delete-confirm-overlay-btns">
            <button className="rt-delete-confirm-btn" onClick={() => handleDeleteCustom(type.id)}>Delete</button>
            <button className="contacts-import-cancel-btn" onClick={e => { e.stopPropagation(); setDeleteConfirmId(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="rt-container">
      <div className="rt-header-row">
        <div>
          <h2>{systemLevel ? 'System Reminders' : 'Reminder Templates'}</h2>
          <p>Choose a reminder to view or edit messages.</p>
        </div>
        <div className="rt-header-actions">
          {/* Reminder-suspend control: org-wide for org admins, self for agents.
              The agent's self-pause is always available (their own persistent setting);
              if the org is also paused, a banner below makes that clear. */}
          {suspendScope === 'org' && onToggleSuspend && (
            <button className={`rt-new-btn rt-pause-btn ${suspended ? 'paused' : ''}`}
              title="Pause reminder sending for every agent in this organization (e.g. for maintenance). While paused, no reminders are sent for any agent. Click to resume."
              onClick={() => onToggleSuspend(!suspended)}>
              {suspended
                ? <><Icon name="pause" size={16} /> <span className="lbl-full">Paused</span><span className="lbl-short">Paused</span></>
                : <><Icon name="pause" size={16} /> <span className="lbl-full">Pause Reminders</span><span className="lbl-short">Pause</span></>}
            </button>
          )}
          {suspendScope === 'self' && onToggleSuspend && (
            <button className={`rt-new-btn rt-pause-btn ${suspended ? 'paused' : ''}`}
              title="Pause sending of your reminders — while paused, all of your reminders are skipped, and it also stops a send already in progress within a couple seconds. Click to resume."
              onClick={() => onToggleSuspend(!suspended)}>
              {suspended
                ? <><Icon name="pause" size={16} /> <span className="lbl-full">Paused</span><span className="lbl-short">Paused</span></>
                : <><Icon name="pause" size={16} /> <span className="lbl-full">Pause Reminders</span><span className="lbl-short">Pause</span></>}
            </button>
          )}
          <button className="rt-new-btn" onClick={createNew}>
            <Icon name="add" size={16} /><span className="lbl-full">Custom Reminder</span><span className="lbl-short">Custom</span>
          </button>
          {/* Import a single reminder (definition + variables + messages) as an owned copy. */}
          <input ref={importInputRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={onImportFile} />
          <button className="rt-new-btn rt-import-btn" title="Import a reminder from a file (creates your own editable copy)"
            onClick={() => { setImportMsg(''); importInputRef.current && importInputRef.current.click(); }}>
            <Icon name="import" size={16} /> Import
          </button>
        </div>
      </div>
      {importMsg && <div className="rt-push-status">{importMsg}</div>}
      {saveNote && <div className="rt-push-status">{saveNote}</div>}

      {suspendScope === 'org' && suspended && (
        <div className="rt-suspend-banner"><Icon name="pause" size={16} /> Reminder processing is <strong>suspended org-wide</strong> — no reminders will be sent for any agent until you resume.</div>
      )}
      {suspendScope === 'self' && orgSuspended && (
        <div className="rt-suspend-banner"><Icon name="pause" size={16} /> Your organization has <strong>paused all reminders</strong> (maintenance). Contact your admin to resume.</div>
      )}
      {suspendScope === 'self' && suspended && (
        <div className="rt-suspend-banner"><Icon name="pause" size={16} /> Your reminders are <strong>paused</strong> — all of your reminders are skipped until you click “Paused” to resume.</div>
      )}

      {isMaster && (
        <div className="rt-master-note">
          <Icon name="wrench" size={16} /> {systemLevel
            ? <>These are the <strong>system-level</strong> reminders. Reminders marked <strong>Public</strong> are copied to each new organization (and to new independent agents) on creation.</>
            : <>These are your organization's <strong>master</strong> reminders. Reminders marked <strong>Public</strong> are copied to each new agent on creation; agents' own edits stay private.</>}
        </div>
      )}



      <div className="rt-grid">
        {/* Base scaffolds first (e.g. Reminder Template) */}
        {baseCards.map(renderCustomCard)}

        {/* System (built-in) reminders */}
        {visibleBuiltins.map(type => {
          const bInherited = customTemplates.find(c => c.builtin && c.id === type.id)?.inherited === true;
          return (
            <div key={type.id} className={`rt-card-wrap ${selected === type.id ? 'active' : ''}`}>
              <button className={`rt-card ${bInherited ? 'rt-card-inherited' : ''} ${selected === type.id ? 'active' : ''}`}
                onClick={() => handleCardClick(type.id)}>
                <span className="rt-card-icon"><Icon name={type.icon} size={20} /></span>
                <span className="rt-card-label">{type.label}</span>
                <span className="rt-card-desc">{type.description}</span>
                {!hasContent(type.id) && noMsgBadge()}
              </button>
              {renderCardToggles(type, true)}
            </div>
          );
        })}

        {/* Other custom reminders */}
        {otherCards.map(renderCustomCard)}
      </div>

      {selected && (
        <div className="rt-detail">
          <div className="rt-detail-header">
            {activeType?.custom && !activeType?.base && !activeType?.inherited ? (
              <span className="rt-detail-title">
                <span className="rt-detail-icon"><Icon name={activeType.icon} size={20} /></span>
                <input className="rt-title-input" value={draftLabel} aria-label="Reminder name"
                  onChange={e => setDraftLabel(e.target.value)} />
              </span>
            ) : (
              <span className="rt-detail-title"><Icon name={activeType?.icon} size={20} /> {activeType?.label}</span>
            )}
            {/* Public/private — buffered like every other field; committed on Save. */}
            {canEditPublic() && (
              <button type="button" className={`rt-pub-toggle rt-detail-pub ${draftPublic ? 'on' : 'off'}`}
                title="Public relays this reminder to the level below (MLS → orgs → agents). It only controls visibility for lower levels — not the content. Buffered: applied when you click Save."
                onClick={() => setDraftPublic(p => !p)}>
                {draftPublic ? <><Icon name="public" size={16} /> Public</> : <><Icon name="private" size={16} /> Private</>}
              </button>
            )}
            {isAdmin && activeType?.base && (
              <span className="rt-base-note" title="System-level scaffold — duplicate it to build a reminder; the copy starts private."><Icon name="lock" size={16} /> System template — can’t be published. Duplicate it to build a reminder (copies start private).</span>
            )}
            {/* Action buttons, right-aligned as a group. */}
            <div className="rt-detail-actions">
              {/* Inherited templates are read-only — the only action is to make an owned copy. */}
              {activeType && !activeType.base && activeType.inherited && (
                <button className="rt-dup-btn" title="Make an editable, assignable copy you own"
                  onClick={() => startDuplicate(activeType.id)}><Icon name="copy" size={16} /> <span className="lbl-full">Make a copy</span><span className="lbl-short">Copy</span></button>
              )}
              {/* Any OWNED reminder can be duplicated — admins AND agents (an agent may
                  copy their own reminders too). Inherited ones use "Make a copy" above. */}
              {activeType && !activeType.inherited && (
                <button className="rt-dup-btn" title="Make a copy of this reminder" onClick={() => startDuplicate(activeType.id)}><Icon name="copy" size={16} /> <span className="lbl-full">Make a copy</span><span className="lbl-short">Copy</span></button>
              )}
              {activeType && !activeType.base && (
                <button className="rt-dup-btn rt-export-btn" title="Export this reminder to a file (definition + variables + messages)"
                  onClick={() => exportReminder(activeType.id)}><Icon name="export" size={16} /> <span className="lbl-full">Export</span><span className="lbl-short">Export</span></button>
              )}
            </div>
          </div>
          {readOnly && (
            <div className="rt-readonly-note">
              <Icon name="lock" size={16} /> This is an inherited template — its content is read-only and stays in sync with the level above.{canEditPublic() && <> You can still choose whether to relay it to the level below with the <strong>Public / Private</strong> toggle, then click <strong>Save</strong> — this only changes visibility, not content.</>} Click <strong>Make a copy</strong> above to get your own editable{isAdmin ? '' : ', assignable'} version.
            </div>
          )}
          {pendingNav && (
            <div className="rt-push-confirm">
              <span>⚠ You have unsaved changes. Save or discard before switching?</span>
              <button className="rt-save-btn" disabled={saving} onClick={async () => { await handleSave(); executePendingNav(pendingNav); }}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="rt-push-discard" onClick={() => { discardCurrentChanges(); executePendingNav(pendingNav); }}>Discard</button>
              <button className="contacts-import-cancel-btn" onClick={() => setPendingNav(null)}>Cancel</button>
            </div>
          )}

          <div className="rt-lang-row">
            <label htmlFor="rt-lang-select" className="rt-lang-label">Language</label>
            <select id="rt-lang-select" className="rt-lang-select" value={language}
              onChange={e => setLanguage(e.target.value)}>
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
          </div>

          {/* Repeating cadence — custom (interval-type) reminders only; buffered + saved
              like the rest. Sets the default interval used when this reminder is assigned. */}
          {canEditInterval() && (
            <div className="rt-lang-row">
              <label htmlFor="rt-interval-select" className="rt-lang-label">Repeat</label>
              <select id="rt-interval-select" className="rt-lang-select" value={draftInterval}
                onChange={e => setDraftInterval(e.target.value)}>
                {ADHOC_INTERVALS.map(iv => (
                  <option key={iv} value={iv}>{INTERVAL_LABELS[iv] || iv}</option>
                ))}
              </select>
              <span className="rt-msg-lang-hint">Default cadence when assigned to contacts.</span>
            </div>
          )}

          <div className="rt-msg-row">
            <label className="rt-msg-label">
              Message
              <span className="rt-msg-lang-hint">({LANGUAGES.find(l => l.code === language)?.label})</span>
            </label>
            {!readOnly && (
              <div className="rt-variables">
                <span className="rt-variables-label">Insert:</span>
                {VARIABLES.filter(v => !activeType?.variables || activeType.variables.includes(v.key)).map(v => (
                  <button key={v.key} className="rt-var-chip"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => insertVariable(v.key, v.label)} title={`Insert ${v.key}`}>
                    + {v.label}
                  </button>
                ))}
              </div>
            )}
            {readOnly ? (
              <div className="rt-readonly-body" dangerouslySetInnerHTML={{ __html: currentMessage ? htmlWithVarPills(currentMessage) : '<em>(no message)</em>' }} />
            ) : (
              <RichEditor ref={editorRef} docKey={msgKey} value={currentMessage} varPills
                onChange={handleMessageChange}
                placeholder={`Enter your ${LANGUAGES.find(l => l.code === language)?.label} message here...`} />
            )}
            <p className="rt-variables-hint">
              Variables like <span className="re-var">Contact</span> will be replaced with real values when the message is sent.
            </p>
          </div>

          {/* System reminders never carry files (System owns no file store, and a file
              borrowed from one MLS wouldn't resolve for the other MLSs it cascades to),
              so the attach picker is hidden at the System level. */}
          {!readOnly && !systemLevel && (() => {
            const lib = visibleFiles(fileKeys);
            return (
              <div className="rt-attach">
                <div className="rt-attach-head">
                  <span className="rt-attach-title"><Icon name="files" size={16} /> Attach files</span>
                  <span className="rt-attach-count">{draftAttachments.length} selected</span>
                </div>
                <p className="rt-attach-hint">Pick from your library and the public files shared by your MLS/org. Files are referenced (stored once), and the selection travels with the reminder when pushed down. Upload files on the <strong>Files</strong> tab.</p>
                {lib.length === 0 ? (
                  <div className="rt-attach-empty">No files available yet — upload some first.</div>
                ) : (
                  <ul className="rt-attach-list">
                    {lib.map(f => (
                      <li key={f.id} className="rt-attach-item">
                        <label className="rt-attach-pick">
                          <input type="checkbox" checked={draftAttachments.includes(f.id)} onChange={() => toggleAttachDraft(f.id)} />
                          <span className="rt-attach-icon"><Icon name="file" size={16} /></span>
                          <span className="rt-attach-name">{f.name}</span>
                        </label>
                        <button type="button" className="rt-attach-view" title="View the file content in a new tab" onClick={() => openFile(f)}><Icon name="view" size={16} /> View</button>
                        <span className="rt-attach-size">{fmtSize(f.size)}</span>
                        {f.inherited && <span className="rt-attach-from">{f.owner.startsWith('mls:') ? 'MLS' : 'Org'}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* Save is shown for editable reminders AND for inherited ones an admin can
              relay (the only editable thing there is the Public/Private flag). */}
          {(!readOnly || canEditPublic()) && (
            <div className="rt-actions">
              {savedKeys[msgKey] && <span className="rt-saved-indicator">✓ Saved</span>}
              <button className="rt-save-btn" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReminderTemplates;
